import type { ElementCompact } from 'xml-js'
import { xml2js } from 'xml-js'
import { gridService } from '../grid/grid.service'
import type {
  FormData,
  FormOverlay,
  ImportItem,
  MenuData,
  MenuItem,
  Panel,
  Tab,
  TreeItem
} from './types'
import { treeNodeIdsEqual } from '@/common/utils/treeTableIds'
import { parseMenuCountValue } from '@/common/utils/formatMenuCountString'
import { normalizeMenuCustomWindowType } from '@/services/menu/menuItemNavigation'
import { extractScriptNodeText } from '@/common/utils/scriptNodeText'
import { readXmlNodeText } from '@/common/utils/pageTabMeta'
import {
  getWebClientExecFromScriptNode,
  hasExplicitWebClientExecAttr,
} from '@/common/utils/webClientExec'
import type { ScriptData } from '@/components/platform/types'
import {
  PLATFORM_SCRIPT_CONSOLE_PREAMBLE,
  installPlatformScriptConsole,
} from '@/common/utils/scriptConsole'

type PanelRowspanSpanInfo = { cell: any; remaining: number }
const JS_IDENTIFIER = /^[A-Za-z_$][\w$]*$/

/** Rhino/Java client-import — выполняется на middle, не компилируется в браузере. */
export function isServerOnlyPlatformImport(rawCode: string): boolean {
  if (!rawCode?.trim()) {
    return false
  }
  if (/\bimportClass\s*\(/.test(rawCode)) {
    return true
  }
  if (/\bimportPackage\s*\(/.test(rawCode)) {
    return true
  }
  if (/\bPackages\./.test(rawCode)) {
    return true
  }
  if (/\bnew\s+[A-Za-z_$][\w$.]*\s*\(\s*[^)]*\)\s*\{/.test(rawCode)) {
    return true
  }
  // After importClass strip: java.net.URI / IDN remain as unqualified names.
  if (/\bnew\s+URI\s*\(/.test(rawCode) || /\bIDN\./.test(rawCode)) {
    return true
  }
  return false
}

export function sanitizePlatformImportScript(script: string): string {
  if (!script) return ''
  return script
    .replace(/importClass\s*\([^)]+\)\s*;?\s*/g, '')
    .replace(/new java.awt.Color\(\d*, \d*, \d*\)/g, (matched) => matched.replace(/new java.awt.Color/, 'rgb'))
    .trim()
    .replace(/;+\s*$/g, '')
}

function sanitizeImportScript(script: string): string {
  return sanitizePlatformImportScript(script)
}

export type ParsedPlatformImports = {
  clientImports: Record<string, ImportItem>
  serverOnlyImportNames: string[]
}

function readPlatformImportRows(imports: any): any[] {
  return Array.isArray(imports)
    ? imports
    : (imports?.fields?.fields?.imports?.value?.rows || [])
}

function readPlatformImportName(row: any, imports: any): string | undefined {
  const name = Array.isArray(imports) ? row?.name : row?.fields?.name?.value
  return name ? String(name) : undefined
}

function readPlatformImportCode(row: any, imports: any): string {
  return Array.isArray(imports) ? (row?.code ?? '') : (row?.fields?.code?.value ?? '')
}

/** Parse platform imports: compile browser-safe, track server-only names. */
export function parsePlatformImports(imports: any): ParsedPlatformImports {
  const rows = readPlatformImportRows(imports)
  const clientImports: Record<string, ImportItem> = {}
  const serverOnlyImportNames: string[] = []

  rows.forEach((row: any) => {
    const name = readPlatformImportName(row, imports)
    if (!name) {
      return
    }

    let code = ''
    try {
      code = readPlatformImportCode(row, imports)
      const sanitized = sanitizeImportScript(code)
      if (isServerOnlyPlatformImport(sanitized)) {
        serverOnlyImportNames.push(name)
        return
      }
      clientImports[name] = compilePlatformImport(name, code)
    } catch (error) {
      console.warn('Failed to eval function ' + name, error, code)
    }
  })

  return { clientImports, serverOnlyImportNames }
}

function extractImportCandidates(importName: string, script: string): string[] {
  const declaredFunctions = [...script.matchAll(/function\s+([A-Za-z_$][\w$]*)\s*\(/g)].map((m) => m[1])
  const declaredFunctionVars = [
    ...script.matchAll(/\b(?:var|let|const)\s+([A-Za-z_$][\w$]*)\s*=\s*function\b/g),
  ].map((m) => m[1])
  const declaredNames = [...new Set([...declaredFunctionVars, ...declaredFunctions])]

  const ordered = [
    ...(JS_IDENTIFIER.test(importName) && declaredNames.includes(importName) ? [importName] : []),
    ...declaredFunctionVars.reverse(),
    ...declaredFunctions.reverse(),
  ]

  return [...new Set(ordered)]
}

function compilePlatformImport(importName: string, rawCode: string): ImportItem {
  const code = sanitizeImportScript(rawCode)
  const isAsync = /\basync\s+function\b/.test(code)
  const candidates = extractImportCandidates(importName, code)
  installPlatformScriptConsole()

  if (candidates.length === 0) {
    return {
      func: new Function(`${PLATFORM_SCRIPT_CONSOLE_PREAMBLE}return function() {\n${code}\n};`)(),
      code,
      async: isAsync,
    }
  }

  const resolverSource = `
${PLATFORM_SCRIPT_CONSOLE_PREAMBLE}
${code}
${candidates.map((name) => `if (typeof ${name} !== 'undefined') return ${name};`).join('\n')}
return function() {
${code}
};
`

  return {
    // eslint-disable-next-line no-new-func
    func: new Function(resolverSource)(),
    code,
    async: isAsync,
  }
}

function parseOverlayInt(value: unknown, defaultVal = 0): number {
  if (value == null) return defaultVal
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const text =
    typeof value === 'object' && value !== null && '_text' in value
      ? String((value as { _text?: unknown })._text ?? '')
      : String(value)
  const n = parseInt(text, 10)
  return Number.isFinite(n) ? n : defaultVal
}

function parseOverlayBool(value: unknown): boolean {
  if (value == null) return false
  const text =
    typeof value === 'object' && value !== null && '_text' in value
      ? String((value as { _text?: unknown })._text ?? '')
      : String(value)
  return text.trim().toLowerCase() === 'true'
}

function looksLikeFormLayoutNode(node: any): boolean {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return false
  }
  return (
    node.panels !== undefined ||
    node.main !== undefined ||
    node.header !== undefined ||
    node.footer !== undefined
  )
}

/** Достаточно ли объекта формы для рендера (main/header/footer/panels). */
export function formHasRenderableLayout(form: any): boolean {
  if (!form || typeof form !== 'object') {
    return false
  }
  const roots: any[] = [form]
  const inner = form.form
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    roots.push(inner)
  }
  for (const f of roots) {
    if (Array.isArray(f.main) && f.main.length > 0) return true
    if (Array.isArray(f.header) && f.header.length > 0) return true
    if (Array.isArray(f.footer) && f.footer.length > 0) return true
    const p = f.panels
    if (p && typeof p === 'object') {
      if (p.panel !== undefined) return true
      if (Object.keys(p).length > 0) return true
    }
  }
  return false
}

/** Панель из разметки без строк элементов (middle отдал ссылку id без тела panel в XML). */
export function formLayoutHasStubPanels(form: unknown): boolean {
  if (!form || typeof form !== 'object') {
    return false
  }
  const visitPanel = (panel: unknown): boolean => {
    if (!panel || typeof panel !== 'object') return false
    const p = panel as Record<string, unknown>
    const pid = p.panelId ?? p.id
    if (pid == null || String(pid).trim() === '') return false
    const rows = p.rows
    return !Array.isArray(rows) || rows.length === 0
  }
  const visitTab = (tab: unknown): boolean => {
    if (!tab || typeof tab !== 'object') return false
    const panelRows = (tab as Record<string, unknown>).panelRows
    if (!Array.isArray(panelRows)) return false
    for (const row of panelRows) {
      if (!Array.isArray(row)) continue
      for (const cell of row) {
        if (visitPanel(cell)) return true
      }
    }
    return false
  }
  const f = form as Record<string, unknown>
  for (const region of [f.main, f.header, f.footer]) {
    if (!Array.isArray(region)) continue
    for (const band of region) {
      if (!Array.isArray(band)) continue
      for (const tab of band) {
        if (visitTab(tab)) return true
      }
    }
  }
  return false
}

export function resolveLayoutPanelRef(cell: unknown): {
  panelId: string
  attributes: Record<string, unknown>
} | null {
  if (!cell || typeof cell !== 'object') {
    return null
  }
  const record = cell as Record<string, unknown>
  const ref = (record.panel ?? record.conditionalPanel) as Record<string, unknown> | undefined
  if (!ref || typeof ref !== 'object') {
    return null
  }
  const panelIdRaw = ref._text
  if (panelIdRaw == null || String(panelIdRaw).trim() === '') {
    return null
  }
  return {
    panelId: String(panelIdRaw).trim(),
    attributes: (
      ref._attributes && typeof ref._attributes === 'object'
        ? ref._attributes
        : {}
    ) as Record<string, unknown>,
  }
}

function isNormalizedFormOverlay(item: unknown): item is FormOverlay {
  return (
    item != null &&
    typeof item === 'object' &&
    'panelRows' in item &&
    'name' in item &&
    Array.isArray((item as FormOverlay).panelRows)
  )
}

/** `<overlays/>` или `{}` после xml-js — не overlay-страница. */
function isEmptyOverlaysContainer(overlays: unknown): boolean {
  if (overlays == null) return true
  if (typeof overlays === 'string') return overlays.trim() === ''
  if (Array.isArray(overlays)) return overlays.length === 0
  if (typeof overlays !== 'object') return true

  const raw = overlays as Record<string, unknown>
  if (raw.overlay == null) {
    const keys = Object.keys(raw).filter((k) => k !== '_attributes')
    return keys.length === 0
  }

  const list = Array.isArray(raw.overlay) ? raw.overlay : [raw.overlay]
  return list.filter((node) => node != null && typeof node === 'object').length === 0
}

function resolvePageAttributes(page: unknown): Record<string, string> {
  if (!page || typeof page !== 'object') {
    return { id: '', description: '', order: '900', lazy: 'false' }
  }
  const p = page as Record<string, unknown>
  const fromAttrs =
    p._attributes && typeof p._attributes === 'object'
      ? (p._attributes as Record<string, string>)
      : {}
  const read = (key: string): string => {
    const fromAttr = fromAttrs[key]
    if (fromAttr != null && fromAttr !== '') return String(fromAttr)
    const direct = p[key]
    if (direct != null && direct !== '') return String(direct)
    return ''
  }
  return {
    id: read('id'),
    description: read('description'),
    order: read('order') || '900',
    lazy: read('lazy') || 'false',
  }
}

function readPageChildText(page: unknown, key: string): string {
  if (!page || typeof page !== 'object') return ''
  return readXmlNodeText((page as Record<string, unknown>)[key])
}

function resolvePageOnClickFields(page: unknown): {
  onClick: string
  web_client_exec?: boolean
} {
  if (!page || typeof page !== 'object') {
    return { onClick: '' }
  }
  const node = (page as { onClick?: unknown }).onClick
  const onClick = extractScriptNodeText(node).trim()
  if (!onClick) {
    return { onClick: '' }
  }
  const scriptNode = node && typeof node === 'object' ? (node as ScriptData) : undefined
  if (hasExplicitWebClientExecAttr(scriptNode)) {
    return { onClick, web_client_exec: getWebClientExecFromScriptNode(scriptNode) }
  }
  return { onClick }
}

function isOverlayLayoutNode(node: unknown): boolean {
  if (!node || typeof node !== 'object') return false
  const n = node as Record<string, unknown>
  if (n.tr != null) return true
  if (n._attributes != null && typeof n._attributes === 'object') return true
  return n.id != null || n.description != null
}

/** Нормализует form.overlays в массив FormOverlay (если пришло с сервера как { overlay: [...] }). */
export function normalizeFormOverlays(form: any, formId: number): void {
  if (!form || typeof form !== 'object') return

  const normalizeRoot = (root: any) => {
    if (!root || root.overlays == null) return

    if (isEmptyOverlaysContainer(root.overlays)) {
      // Уже [] — не переприсваивать: иначе deep-watch в FormOverlaysBlock зацикливается.
      if (!Array.isArray(root.overlays) || root.overlays.length > 0) {
        root.overlays = []
      }
      return
    }

    if (Array.isArray(root.overlays)) {
      if (root.overlays.length === 0) return
      if (isNormalizedFormOverlay(root.overlays[0])) return
      root.overlays = parseService.getOverlays({
        overlays: { overlay: root.overlays },
        panels: root.panels ?? form.panels,
        formId,
      })
      return
    }

    let panels = root.panels ?? form.panels
    const panelsLookLikeMap =
      panels &&
      typeof panels === 'object' &&
      !Array.isArray(panels) &&
      panels.panel == null
    if (!panelsLookLikeMap && panels?.panel != null) {
      panels = parseService.getPanels(panels.panel)
      root.panels = panels
    }

    root.overlays = parseService.getOverlays({
      overlays: root.overlays,
      panels,
      formId,
    })
  }

  normalizeRoot(form)
  if (form.form && typeof form.form === 'object' && !Array.isArray(form.form)) {
    normalizeRoot(form.form)
    if (!Array.isArray(form.overlays) && Array.isArray(form.form.overlays)) {
      form.overlays = form.form.overlays
    }
  }
}

/** Корень формы в compact xml-js: обычно {@code form}, но бывает другой регистр или одна обёртка над реальной разметкой */
function extractFormRootFromCompact(compact: Record<string, any> | null | undefined): any {
  if (!compact || typeof compact !== 'object') {
    return undefined
  }
  if (compact.form) {
    return compact.form
  }
  for (const key of Object.keys(compact)) {
    if (key.startsWith('_')) {
      continue
    }
    if (key.toLowerCase() === 'form') {
      return compact[key]
    }
  }
  const contentKeys = Object.keys(compact).filter(
    (k) => !k.startsWith('_') && k !== '_declaration' && !k.startsWith('?'),
  )
  if (contentKeys.length === 1) {
    const inner = compact[contentKeys[0]]
    if (looksLikeFormLayoutNode(inner)) {
      return inner
    }
  }
  return undefined
}

function extractEmbeddedXmlFromCompactNode(node: any): string | null {
  if (!node || typeof node !== 'object') {
    return null
  }
  const t = node._text ?? node._cdata
  if (typeof t !== 'string') {
    return null
  }
  const trimmed = t.trim()
  return trimmed.startsWith('<') ? trimmed : null
}

/**
 * Вставляет плейсхолдеры в строки td панели по rowspan/colspan (семантика HTML-таблицы),
 * чтобы число логических колонок совпадало между строками и совпадало со Swing GridBag.
 */
function expandPanelElementRowsForRowspan(rows: any[][]): any[][] {
  const rowspanTracker: PanelRowspanSpanInfo[][] = []

  return rows.map((row, rowIndex) => {
    if (!Array.isArray(row) || row.length === 0) {
      return row
    }

    if (!rowspanTracker[rowIndex]) {
      rowspanTracker[rowIndex] = []
    }

    const processedCells: any[] = []
    let tdIndex = 0
    let columnIndex = 0

    while (tdIndex < row.length || rowspanTracker[rowIndex][columnIndex]) {
      if (rowspanTracker[rowIndex][columnIndex]) {
        const spanInfo = rowspanTracker[rowIndex][columnIndex]

        processedCells.push({
          _isRowspanPlaceholder: true,
          _anchorCell: spanInfo.cell,
          _attributes: {},
        })

        if (spanInfo.remaining > 1) {
          if (!rowspanTracker[rowIndex + 1]) {
            rowspanTracker[rowIndex + 1] = []
          }
          rowspanTracker[rowIndex + 1][columnIndex] = {
            cell: spanInfo.cell,
            remaining: spanInfo.remaining - 1,
          }
        }

        columnIndex++
        continue
      }

      if (tdIndex < row.length) {
        const cell = row[tdIndex]
        cell._attributes = cell._attributes || {}

        const colspan = parseInt(String(cell._attributes.colspan || '1'), 10) || 1
        const rowspan = parseInt(String(cell._attributes.rowspan || '1'), 10) || 1

        if (rowspan > 1) {
          cell._attributes.rowspan = rowspan
          for (let i = 1; i < rowspan; i++) {
            if (!rowspanTracker[rowIndex + i]) {
              rowspanTracker[rowIndex + i] = []
            }
            for (let j = 0; j < colspan; j++) {
              rowspanTracker[rowIndex + i][columnIndex + j] = {
                cell,
                remaining: rowspan - i,
              }
            }
          }
        }

        processedCells.push(cell)
        for (let j = 1; j < colspan; j++) {
          processedCells.push({
            _isColspanPlaceholder: true,
            _anchorCell: cell,
            _attributes: {},
          })
        }
        tdIndex++
        columnIndex += colspan
      } else {
        columnIndex++
      }
    }

    return processedCells
  })
}

export const parseService = {
  /**
   * Парсит меню, полученное с сервера
   */
  getMenu(data: string): MenuData {
    const cdataMap = new Map<number, string>()
    const cdataRegex = /<in_unidata[^>]*><!\[CDATA\[(.*?)\]\]><\/in_unidata>/gis
    let match
    while ((match = cdataRegex.exec(data)) !== null) {
      const cdataContent = match[1]
      // Find the branch_id for this CDATA by looking backwards in the XML
      // Match both <branch_id> and <BRANCH_ID> (case-insensitive)
      const beforeMatch = data.substring(0, match.index)
      const branchIdMatch = beforeMatch.match(/<branch_id[^>]*>(\d+)<\/branch_id>/i)
      if (branchIdMatch) {
        const branchId = Number(branchIdMatch[1])
        cdataMap.set(branchId, cdataContent)
      }
    }

    const typesToHide = [
      'hidden',
      'user-status-button',
      'service-toolbar-button',
    ]

    const isVisible = (item: MenuItem): boolean => {
      return !typesToHide.includes(item.item_type)
    }

    const isTopMenu = (item: MenuItem): boolean => item.item_type === 'toolbar_button'

    const buildTopMenu = (items: MenuItem[]): MenuItem[] => {
      return items.filter(i => i.item_type === 'toolbar_button')
    }

    const buildSideMenu = (items: MenuItem[]): MenuItem[] => {
      // First, build a set of all branch_ids that exist for parent lookup
      const allBranchIds = new Set<number>()
      items.forEach(item => allBranchIds.add(item.branch_id))

      const itemsByBranchId = new Map<number, MenuItem>()
      const duplicateItems = new Set<MenuItem>()

      items.forEach(item => {
        const existing = itemsByBranchId.get(item.branch_id)
        if (!existing) {
          itemsByBranchId.set(item.branch_id, item)
        } else {
          const itemIsRoot = item.parent === 0
          const existingIsRoot = existing.parent === 0
          const itemParentExists = allBranchIds.has(item.parent)
          const existingParentExists = allBranchIds.has(existing.parent)

          if (itemIsRoot && !existingIsRoot) {
            itemsByBranchId.set(item.branch_id, item)
            duplicateItems.add(existing)
          } else if (existingIsRoot && !itemIsRoot) {
            duplicateItems.add(item)
          } else if (itemParentExists && !existingParentExists) {
            itemsByBranchId.set(item.branch_id, item)
            duplicateItems.add(existing)
          } else if (existingParentExists && !itemParentExists) {
            duplicateItems.add(item)
          } else {
            itemsByBranchId.set(item.branch_id, item)
            duplicateItems.add(existing)
          }
        }
      })

      const itemsMap = new Map<number, MenuItem>()
      itemsByBranchId.forEach((item, branchId) => {
        itemsMap.set(branchId, item)
      })

      const childrenSet = new Set<number>()

      for (const child of items) {
        if (child.parent !== 0 && !childrenSet.has(child.branch_id) && !duplicateItems.has(child)) {
          if (isTopMenu(child)) {
            continue
          }
          const parent = itemsMap.get(child.parent)
          if (!parent) {
            continue
          }

          if (!Array.isArray(parent.children)) {
            parent.children = []
          }
          parent.children.push(child)
          childrenSet.add(child.branch_id)
        }
      }
      // Sort children arrays for each parent
      const sortChildrenRecursively = (item: MenuItem) => {
        if (item.children && item.children.length > 0) {
          item.children.sort((a, b) => a.sort - b.sort)
          item.children.forEach(sortChildrenRecursively)
        }
      }
      // Only sort root items
      const rootItems = items.filter(i => i.parent === 0 && !isTopMenu(i))
      rootItems.forEach(sortChildrenRecursively)

      // drop branches with no target (chainId==0), including empty folders
      const dropEmptyMenuBranches = (nodes: MenuItem[]): MenuItem[] => {
        const out: MenuItem[] = []
        for (const item of nodes) {
          item.children = item.children?.length ? dropEmptyMenuBranches(item.children) : []
          const hasChain =
            item.target_chain_id != null && Number(item.target_chain_id) > 0
          const hasInUnidata = !!(item.in_unidata && String(item.in_unidata).trim())
          if (item.children.length > 0 || hasChain || hasInUnidata) {
            out.push(item)
          }
        }
        return out
      }

      return dropEmptyMenuBranches(rootItems)
    }

    const menuData = xml2js(data, {
      compact: true,
      ignoreCdata: false,
      ignoreComment: true,
      textKey: '_text',
      cdataKey: '_cdata',
      alwaysArray: false,
      alwaysChildren: false
    }) as ElementCompact

    const getText = (element: any): string | undefined => {
      if (!element) return undefined
      if (typeof element === 'string') return element
      if (typeof element === 'number') return String(element)

      if (element._cdata !== undefined) {
        if (Array.isArray(element._cdata)) {
          return element._cdata.map((t: any) => String(t || '')).join('')
        }
        return String(element._cdata || '')
      }

      if (element._text !== undefined) {
        if (Array.isArray(element._text)) {
          return element._text.map((t: any) => String(t || '')).join('')
        }
        return String(element._text || '')
      }

      if (Array.isArray(element)) {
        return element.map(e => getText(e)).filter(Boolean).join('')
      }

      if (typeof element === 'object') {
        for (const key of ['_cdata', '_text', 'text', 'value']) {
          if (element[key] !== undefined) {
            return getText(element[key])
          }
        }
      }
      return undefined
    }

    let menu: MenuItem[] = (menuData.branches.branch as ElementCompact[]).map(item => {
      // Try to get IN_UNIDATA from various possible structures
      let inUnidata: string | undefined = undefined

      const inUnidataElement = item.in_unidata || item.IN_UNIDATA

      if (inUnidataElement) {
        if (inUnidataElement._cdata !== undefined) {
          inUnidata = Array.isArray(inUnidataElement._cdata)
            ? inUnidataElement._cdata.map((t: any) => String(t || '')).join('')
            : String(inUnidataElement._cdata || '')
        }
        // Fall back to _text if _cdata not available
        else if (inUnidataElement._text !== undefined) {
          inUnidata = Array.isArray(inUnidataElement._text)
            ? inUnidataElement._text.map((t: any) => String(t || '')).join('')
            : String(inUnidataElement._text || '')
        }
        // If it's a direct string
        else if (typeof inUnidataElement === 'string') {
          inUnidata = inUnidataElement
        }
        // Use getText as fallback
        else {
          inUnidata = getText(inUnidataElement)
        }
      }

      const branchId = Number(getText(item.branch_id) || getText(item.BRANCH_ID) || 0)

      const finalInUnidata = cdataMap.has(branchId) ? cdataMap.get(branchId) : inUnidata

      const countRaw = getText(item.count)
      const countValue = parseMenuCountValue(countRaw)

      const hideTitleRaw = getText(item.hide_title) ?? getText((item as any).hideTitle)
      const hideTitle = hideTitleRaw === '1' || hideTitleRaw === 'true' || hideTitleRaw === 'yes'
      const openInNewTabRaw =
        getText(item.open_in_new_tab) ??
        getText((item as any).openInNewTab) ??
        getText((item as any).OPEN_IN_NEW_TAB)
      const openInNewTab =
        openInNewTabRaw === '1' ||
        openInNewTabRaw === 'true' ||
        openInNewTabRaw === 'yes'

      const openRaw = getText(item.open) ?? getText((item as any).OPEN)
      const menuOpenDefault =
        openRaw === undefined || openRaw === ''
          ? true
          : openRaw === '1' || openRaw === 'true' || openRaw === 'yes'

      const customWindowTypeRaw =
        getText(item.custom_window_type) ??
        getText((item as any).customWindowType) ??
        getText((item as any).CUSTOM_WINDOW_TYPE)
      const customWindowType = normalizeMenuCustomWindowType(customWindowTypeRaw)

      return {
        branch_id: branchId,
        parent: Number(getText(item.parent) || getText(item.PARENT) || 0),
        sort: Number(getText(item.sort) || getText(item.SORT) || 0),
        name: getText(item.name) || getText(item.NAME) || "",
        item_type: getText(item.item_type) || getText(item.ITEM_TYPE) || "",
        icon: getText(item.icon) || getText(item.ICON) || "",
        platform: getText(item.platform) || getText(item.PLATFORM) || "",
        in_unidata: finalInUnidata,
        target_chain_id: (() => {
          const chainIdText = getText(item.target_chain_id) || getText(item.TARGET_CHAIN_ID)
          if (!chainIdText) return undefined
          const chainId = Number(chainIdText)
          return chainId > 0 ? chainId : undefined
        })(),
        openInNewTab: openInNewTab || undefined,
        hideTitle: hideTitle || undefined,
        customWindowType,
        custom_window_type: customWindowTypeRaw || undefined,
        ...(menuOpenDefault === false ? { open: false } : {}),
        children: [],
        count: countValue
      }
    })

    menu = menu.filter(isVisible).sort((a, b) => a.sort - b.sort)

    const rawMenu = [...menu]
    const sideMenu = buildSideMenu(menu)
    const topMenu = buildTopMenu(menu)

    return { sideMenu, topMenu, rawMenu }
  },

  /**
   * Парсит платформенные импорты (imports)
   * TODO: переделать на работу через сериализатор (UniDataTable)
   * зафиксировать тип imports!
   */
  parsePlatformImports,

  getImports(imports: any): Record<string, ImportItem> {
    return parsePlatformImports(imports).clientImports
  },

  /**
   * Парсит данные формы (XML-строка или уже десериализованный JSON в форме xml-js / вложенный {@code form}).
   */
  getForm(data: string | Record<string, any>, formId: number): FormData {
    try {
      let form: any

      if (data == null) {
        return {} as FormData
      }

      if (typeof data === 'string') {
        let trimmed = data.trim()
        if (trimmed.charCodeAt(0) === 0xfeff) {
          trimmed = trimmed.slice(1).trim()
        }
        if (!trimmed) {
          return {} as FormData
        }
        if (trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(trimmed) as Record<string, any>
            if (parsed && typeof parsed === 'object') {
              return this.getForm(parsed, formId)
            }
          } catch {
            /* не JSON — возможно XML с { в тексте; продолжаем как XML */
          }
        }
        const compact = xml2js(trimmed, { compact: true }) as unknown as Record<string, any>
        form = extractFormRootFromCompact(compact)
        if (form == null) {
          return {} as FormData
        }
      } else {
        const obj = data as Record<string, any>
        if (typeof obj.form === 'string') {
          return this.getForm(obj.form, formId)
        }
        const inner = obj.form
        const innerLooksLikeXmlForm =
          inner &&
          typeof inner === 'object' &&
          !Array.isArray(inner) &&
          (inner.panels !== undefined ||
            inner.main !== undefined ||
            inner.header !== undefined ||
            inner.footer !== undefined)
        const outerLooksLikeXmlForm =
          obj.panels !== undefined ||
          obj.main !== undefined ||
          obj.header !== undefined ||
          obj.footer !== undefined
        if (innerLooksLikeXmlForm && !outerLooksLikeXmlForm) {
          form = inner
        } else {
          form = obj
        }
      }

      if (!form || typeof form !== 'object') {
        return {} as FormData
      }

      // Тело XML в _text / _cdata (compact xml-js) у корня или вложенного form
      const xmlBody =
        extractEmbeddedXmlFromCompactNode(form) ||
        (form.form ? extractEmbeddedXmlFromCompactNode(form.form) : null)
      if (xmlBody) {
        return this.getForm(xmlBody, formId)
      }

      if (form.panels?.panel !== undefined) {
        form.panels = this.getPanels(form.panels.panel)
      }

      if (form.functions) {
        // Уже распарсенная строка (re-parse / borrow form) — не затирать через ._text.
        form.functions =
          typeof form.functions === 'string'
            ? form.functions
            : form.functions._text || form.functions
      }

      if (form.onServerEvent) {
        form.onServerEvent = form.onServerEvent._text || form.onServerEvent
      }

      const { panels, header, footer, main } = form

      // Раньше хватало main[0] instanceof Array — для [[]] это давало «уже собрано» и пустой FormSection.
      const mainAlreadyBuilt =
        Array.isArray(main) &&
        main.length > 0 &&
        main.every((region: unknown) => Array.isArray(region) && region.length > 0)

      if (!mainAlreadyBuilt) {
        if (main && typeof main === 'object' && Object.keys(main).length > 0) {
          const mainArr = Array.isArray(main) ? main : [main]
          form.main = mainArr.map((m: any) => {
            const tabs = this.getTabs({ panels, pages: m.page, formId })
            if (m?._attributes && tabs) {
              ;(tabs as Tab[] & { _attributes?: Record<string, string> })._attributes =
                m._attributes
            }
            return tabs
          })
          form.main = this.buildMainGrid(form.main)
        } else {
          form.main = []
        }
      }

      if (!Array.isArray(header)) {
        form.header = this.getTabs({ pages: header?.page, panels, formId })
      }

      if (!Array.isArray(footer)) {
        form.footer = this.getTabs({ pages: footer?.page, panels, formId })
      }

      const panelsMap = form.panels
      const hasPanels =
        panelsMap != null &&
        typeof panelsMap === 'object' &&
        !Array.isArray(panelsMap) &&
        Object.keys(panelsMap).length > 0
      const mainHasNoTabs =
        !Array.isArray(form.main) ||
        form.main.length === 0 ||
        form.main.every((region: any) => !Array.isArray(region) || region.length === 0)
      const mainEmptyBeforePanelsFallback = mainHasNoTabs
      const hasNonMainLayoutTabs =
        (Array.isArray(form.header) && form.header.length > 0) ||
        (Array.isArray(form.footer) && form.footer.length > 0)
      let usedPanelsFallback = false
      if (hasPanels && mainHasNoTabs && !hasNonMainLayoutTabs) {
        usedPanelsFallback = true
        const syntheticPage = {
          _attributes: {
            id: '__fallback_main__',
            description: '\u00a0',
            order: '0',
          },
          tr: Object.keys(panelsMap as Record<string, unknown>).map((panelId) => ({
            td: { panel: { _text: panelId } },
          })),
        }
        form.main = [this.getTabs({ panels: panelsMap, pages: syntheticPage, formId })]
        form.main = this.buildMainGrid(form.main)
      }

      if (form.overlays != null) {
        if (isEmptyOverlaysContainer(form.overlays)) {
          form.overlays = []
        } else if (!Array.isArray(form.overlays)) {
          form.overlays = this.getOverlays({ overlays: form.overlays, panels: form.panels, formId })
        }
      }

      return form as FormData
    } catch (error) {
      console.error("Form parsing error:", error)
      return {} as FormData
    }
  },

  /**
   * Преобразовывает массив панелей формы
   */
  getPanels(panels: any): Record<string, Panel> {
    if (!Array.isArray(panels)) panels = [panels]

    return panels.reduce((acc: Record<string, Panel>, panel: any) => {
      const id = panel._attributes.id
      if (!panel.tr) return acc

      const rows = Array.isArray(panel.tr)
        ? panel.tr.map((i: any) => {
          if (!i.td) return []
          return Array.isArray(i.td) ? i.td : [i.td]
        }).filter((row: any) => row.length > 0)
        : [Array.isArray(panel.tr.td) ? panel.tr.td : [panel.tr.td]]

      // Make sure all cells have _attributes
      const sanitizedRows = rows.map((row: any) =>
        row.map((cell: any) => {
          if (!cell || typeof cell !== 'object') {
            return { _attributes: {} }
          }
          if (!cell._attributes) {
            cell._attributes = {}
          }
          return cell
        })
      )

      const expandedRows = expandPanelElementRowsForRowspan(sanitizedRows)

      const onServerEventRaw = panel.onServerEvent
      const onServerEvent =
        typeof onServerEventRaw === 'string'
          ? onServerEventRaw
          : onServerEventRaw?._text || onServerEventRaw?._cdata || undefined

      const panelObj: Panel = {
        id,
        level: 'panel',
        attrs: panel._attributes,
        rows: gridService.setColsByGridWithColspan(expandedRows),
        identificators: Array.isArray(panel.element)
          ? panel.element
          : panel.element
            ? [panel.element]
            : [],
        panelId: id,
        formId: 0 // будет установлено позже
      }
      if (typeof onServerEvent === 'string' && onServerEvent.trim()) {
        panelObj.onServerEvent = onServerEvent
      }

      acc[id] = panelObj
      return acc
    }, {})
  },

  /**
   * Вызывает построение сетки для каждого из множественных main'ов
   */
  buildMainGrid(mainArr: any[] = []): any[] {
    mainArr.forEach(main => {
      main.forEach((panel: any) => {
        if (panel.panelRows && Array.isArray(panel.panelRows)) {
          panel.panelRows = gridService.setColsByGrid(panel.panelRows)
          panel.panelRows
            .filter((i: any) => i?.element?.type?._text !== 'LineDelimiter')
            .forEach((panelRow: any) => {
              if (Array.isArray(panelRow)) {
                panelRow.forEach((panel: any) => {
                  if (panel && panel.rows && Array.isArray(panel.rows)) {
                    panel.rows = gridService.setColsByGridWithColspan(panel.rows)
                  }
                })
              }
            })
        }
      })
    })

    return mainArr
  },

  /**
   * Восстанавливает древовидную структуру
   */
  restoreTree(items: TreeItem[]): TreeItem[] {
    const traverseParent = (items: TreeItem[], child: TreeItem, childIdx: number): boolean => {
      const parentId = child.fields.parentId?.value

      for (let i = 0; i < items.length; i++) {
        if (!items[i].fields.isAllowChildren.value) continue

        if (treeNodeIdsEqual(items[i].fields._id?.value, parentId)) {
          if (items[i].fields.items) {
            items[i].fields.items?.value.rows.push(child)
          } else {
            items[i].fields.items = { value: { rows: [child] } }
          }
          items.splice(childIdx, 1)
          return true
        } else if (items[i].fields.items?.value?.rows) {
          const nestedRows = items[i].fields.items!.value.rows
          if (traverseParent(nestedRows, child, childIdx)) {
            return true
          }
        }
      }
      return false
    }

    for (let i = 0; i < items.length; i++) {
      if (!items[i].fields.parentId?.value) continue
      if (traverseParent(items, items[i], i)) i--
    }

    return [...items]
  },

  findParent(tree: TreeItem[], parentId: string): TreeItem | null {
    if (Array.isArray(tree)) {
      for (const el of tree) {
        // Проверить данный узел
        if (treeNodeIdsEqual(el.fields._id?.value, parentId)) {
          return el
          // Проверить потомки
        } else if (Array.isArray(el.fields?.items?.value?.rows)) {
          const parent = this.findParent(el.fields.items.value.rows, parentId)
          if (parent) return parent
        }
      }
    }
    return null
  },

  getTabs({ pages, panels, formId }: { pages: any, panels: any, formId: number }): Tab[] {
    if (!pages) return []
    if (!Array.isArray(pages)) pages = [pages]

    return pages
      .filter((i: any) => !!i)
      .map((page: any) => {
        // Handle pages without tr elements (empty pages)
        const tr = page.tr
          ? (Array.isArray(page.tr) ? page.tr : [page.tr])
          : []

        // Track cells occupied by rowspan from previous rows
        const rowspanTracker: Array<Array<{ panel: any; remaining: number }>> = []

        const panelRows = tr.map((row: any, rowIndex: number) => {
          const td = Array.isArray(row.td) ? row.td : [row.td]

          // Initialize tracker for this row
          if (!rowspanTracker[rowIndex]) {
            rowspanTracker[rowIndex] = []
          }

          const processedCells: any[] = []
          let tdIndex = 0
          let columnIndex = 0

          while (tdIndex < td.length || rowspanTracker[rowIndex][columnIndex]) {
            // Check if this column is occupied by a rowspan from a previous row
            if (rowspanTracker[rowIndex][columnIndex]) {
              const spanInfo = rowspanTracker[rowIndex][columnIndex]

              // Placeholder for cell covered by rowspan
              processedCells.push({
                _isRowspanPlaceholder: true,
                _originalPanel: spanInfo.panel,
                _attributes: { cols: spanInfo.panel._attributes?.cols || 100 }
              })

              // Track for next row if rowspan continues
              if (spanInfo.remaining > 1) {
                if (!rowspanTracker[rowIndex + 1]) {
                  rowspanTracker[rowIndex + 1] = []
                }
                rowspanTracker[rowIndex + 1][columnIndex] = {
                  panel: spanInfo.panel,
                  remaining: spanInfo.remaining - 1
                }
              }

              columnIndex++
              continue
            }

            // Process current td
            if (tdIndex < td.length) {
              const cell = td[tdIndex]

              const layoutRef = resolveLayoutPanelRef(cell)
              if (layoutRef) {
                const panel = panels[layoutRef.panelId] || {
                  _attributes: {},
                  level: "panel",
                  rows: [],
                  identificators: [],
                  panelId: '',
                  formId
                }
                panel._attributes = panel._attributes || {}
                panel.panelId =
                  panel.id != null && String(panel.id).trim() !== ''
                    ? panel.id
                    : layoutRef.panelId
                panel.formId = formId
                Object.assign(panel._attributes, cell._attributes)
                Object.assign(panel._attributes, layoutRef.attributes)

                // Handle rowspan
                const rowspan = parseInt(cell._attributes?.rowspan) || 1
                if (rowspan > 1) {
                  // Mark panel as having rowspan
                  panel._attributes.rowspan = rowspan

                  // Track this panel for next rows
                  for (let i = 1; i < rowspan; i++) {
                    if (!rowspanTracker[rowIndex + i]) {
                      rowspanTracker[rowIndex + i] = []
                    }
                    rowspanTracker[rowIndex + i][columnIndex] = {
                      panel: panel,
                      remaining: rowspan - i
                    }
                  }
                }

                processedCells.push(panel)
              } else if (cell?.element) {
                // Page/overlay cell with inline <element> (no <panel> ref) — Swing FormBuilder
                // creates the control directly; wrap in a simple panel so Label/etc. render.
                // panelId = element XML id so Label registration / setVisible work (empty id skips register).
                const inlineElementId = String(
                  cell.element?._attributes?.id ?? cell.element?.id ?? '',
                ).trim()
                const elementCell = {
                  _attributes: cell._attributes ? { ...cell._attributes } : {},
                  element: cell.element,
                }
                processedCells.push({
                  _attributes: {
                    ...(cell._attributes || {}),
                    style: 'simple',
                  },
                  level: 'panel',
                  rows: [[elementCell]],
                  identificators: [],
                  panelId: inlineElementId,
                  formId,
                  /** Page-level element wrap — hide whole panel when element shell is hidden. */
                  _inlineElement: true,
                })
              } else {
                // Empty cell without panel reference
                processedCells.push({
                  _attributes: cell._attributes || {},
                  level: "panel",
                  rows: [],
                  identificators: [],
                  panelId: '',
                  formId
                })
              }

              tdIndex++
            }

            columnIndex++
          }

          return processedCells
        })

        const { onClick, web_client_exec } = resolvePageOnClickFields(page)
        const pageAttrs = resolvePageAttributes(page)
        const jsName = readPageChildText(page, 'name')
        const image = readPageChildText(page, 'image')
        const style = readPageChildText(page, 'style')

        const tab: Tab = {
          name: pageAttrs.description || 'EMPTY',
          id: pageAttrs.id,
          order: parseInt(pageAttrs.order, 10) || 900,
          lazy: pageAttrs.lazy === 'true',
          panelRows,
        }
        if (jsName) tab.jsName = jsName
        if (image) tab.image = image
        if (style) tab.style = style
        if (onClick) {
          tab.onClick = onClick
        }
        if (web_client_exec !== undefined) {
          tab.web_client_exec = web_client_exec
        }
        return tab
      })
      .sort((a: any, b: any) => a.order - b.order)
  },

  getOverlays({
    overlays,
    panels,
    formId,
  }: {
    overlays: any
    panels: any
    formId: number
  }): FormOverlay[] {
    if (!overlays || isEmptyOverlaysContainer(overlays)) return []

    const overlayNodes = overlays.overlay
      ? Array.isArray(overlays.overlay)
        ? overlays.overlay
        : [overlays.overlay]
      : Array.isArray(overlays)
        ? overlays
        : [overlays]

    return overlayNodes
      .filter((node: any) => isOverlayLayoutNode(node))
      .map((node: any) => {
        const tabs = this.getTabs({ pages: node, panels, formId })
        const tab = tabs[0] ?? { panelRows: [], lazy: false }
        let panelRows = tab.panelRows ?? []

        if (Array.isArray(panelRows) && panelRows.length > 0) {
          panelRows = gridService.setColsByGrid(panelRows)
          panelRows
            .filter((row: any) => row?.element?.type?._text !== 'LineDelimiter')
            .forEach((panelRow: any) => {
              if (Array.isArray(panelRow)) {
                panelRow.forEach((panel: any) => {
                  if (panel?.rows && Array.isArray(panel.rows)) {
                    panel.rows = gridService.setColsByGridWithColspan(panel.rows)
                  }
                })
              }
            })
        }

        const id = String(node._attributes?.id ?? '')
        const nameRaw = node.name?._text ?? node.name
        const name = String(nameRaw ?? id).trim() || id

        const onClick = extractScriptNodeText(node?.onClick).trim()

        const overlay: FormOverlay = {
          id,
          name,
          description: String(node._attributes?.description ?? name),
          lazy: node._attributes?.lazy === 'true' || node._attributes?.lazy === true,
          posX: parseOverlayInt(node.posX),
          posY: parseOverlayInt(node.posY),
          width: parseOverlayInt(node.width),
          height: parseOverlayInt(node.height),
          isDialog: parseOverlayBool(node.isDialog),
          panelRows,
        }
        if (onClick) {
          overlay.onClick = onClick
        }
        return overlay
      })
  },

  getCommand(cmd: string): string {
    const importClass = (cmd: string) => cmd.replace(/importClass(.*)/g, '')
    const javaColors = (cmd: string) => cmd.replace(/new java.awt.Color\(\d*, \d*, \d*\)/g, matched => {
      return matched.replace(/new java.awt.Color/, 'rgb')
    })

    const transformers = [importClass, javaColors]
    cmd = transformers.reduce((acc, transformer) => transformer(acc), cmd)

    // Generate huge log! p.shvets
    // if (cmd.match('data.')) {
    //   console.log('CONDITION CMD: ', cmd)
    // }

    return cmd
  },

  /**
   * Evaluates template literals in strings (like Velocity templates)
   * Supports: #if($var)...#{end}, #if(${var})...#{end}, #if(==40)#elseif(==46)#else...#{end}, ${var}, $var, ${_.getFieldAsString('x','')}
   * For #if(==N) / #elseif(==N) the compared value is taken from resolver('_').
   * @param template - Template string with variables
   * @param resolver - Function to resolve variable values (use '_' for value compared in #if(==N))
   * @returns Evaluated string
   */
  literator(template: string, resolver: (varName: string) => any): string {
    if (!template || typeof template !== 'string') {
      return template || ''
    }

    let result = template

    const fieldCallRegex =
      /\$\{\s*_\.\s*(getFieldAsString|getFieldAsLong|getFieldAsBoolean|getField)\s*\(\s*(['"])([^'"]+)\2\s*(?:,\s*(['"])([^'"]*)\4)?\s*\)\s*\}/g
    result = result.replace(fieldCallRegex, (_match, _method, _quote, fieldName, _defaultQuote, defaultValue) => {
      const value = resolver(fieldName)
      if (value !== null && value !== undefined && value !== '') {
        return String(value)
      }
      if (defaultValue !== undefined) {
        return String(defaultValue)
      }
      return ''
    })

    const parseVelocityCompareConstant = (
      singleQuoted: string | undefined,
      doubleQuoted: string | undefined,
      numeric: string | undefined,
    ): string | number => {
      if (singleQuoted !== undefined) return singleQuoted
      if (doubleQuoted !== undefined) return doubleQuoted
      if (numeric !== undefined && numeric !== '') return Number(numeric)
      return ''
    }

    const valuesEqualForTemplate = (actual: unknown, expected: string | number): boolean => {
      if (typeof expected === 'number') {
        const numCompare = Number(actual)
        return numCompare === expected || String(actual).trim() === String(expected)
      }
      return String(actual).trim() === String(expected).trim()
    }

    const velocityCompareConstPattern = `(?:'([^']*)'|"([^"]*)"|(\\d+))`

    // #if(${menuId}==424)|#if(${statusId}=='draft')#elseif(...)#{else}...#{end}
    const varCompareIfRegex = new RegExp(
      `#if\\s*\\(\\s*(?:\\$\\{\\s*(\\w+)\\s*\\}|\\$(\\w+))\\s*==\\s*${velocityCompareConstPattern}\\s*\\)([\\s\\S]*?)#(?:\\{end\\}|end\\b)`,
      'gi',
    )
    result = result.replace(
      varCompareIfRegex,
      (_fullBlock, braceVarName, plainVarName, firstConstSingle, firstConstDouble, firstConstNum, blockInner) => {
      const firstVarName = (braceVarName ?? plainVarName).trim()
      const firstConst = parseVelocityCompareConstant(firstConstSingle, firstConstDouble, firstConstNum)
      const varCompareDirectiveRegex = new RegExp(
        `#(?:elseif\\s*\\(\\s*(?:\\$\\{\\s*(\\w+)\\s*\\}|\\$(\\w+))\\s*==\\s*${velocityCompareConstPattern}\\s*\\)|\\{else\\}|else\\b)`,
        'gi',
      )
      const branches: { type: 'varEq' | 'else'; varName?: string; value?: string | number; content: string }[] = []
      let pos = 0
      let d
      while ((d = varCompareDirectiveRegex.exec(blockInner)) !== null) {
        const textBefore = blockInner.slice(pos, d.index)
        if (branches.length === 0) {
          branches.push({
            type: 'varEq',
            varName: firstVarName,
            value: firstConst,
            content: textBefore,
          })
        } else {
          branches[branches.length - 1].content = textBefore
        }
        const elseifVar = d[1] ?? d[2]
        const elseifConst = parseVelocityCompareConstant(d[3], d[4], d[5])
        if (elseifVar !== undefined && elseifConst !== '') {
          branches.push({
            type: 'varEq',
            varName: elseifVar.trim(),
            value: elseifConst,
            content: '',
          })
        } else {
          branches.push({ type: 'else', content: '' })
        }
        pos = d.index + d[0].length
      }
      if (branches.length === 0) {
        branches.push({
          type: 'varEq',
          varName: firstVarName,
          value: firstConst,
          content: blockInner,
        })
      } else {
        branches[branches.length - 1].content = blockInner.slice(pos)
      }
      let chosen = ''
      for (const b of branches) {
        if (b.type === 'varEq') {
          const val = resolver(b.varName!)
          if (valuesEqualForTemplate(val, b.value!)) {
            chosen = b.content
            break
          }
        } else {
          chosen = b.content
          break
        }
      }
      return chosen
    },
    )

    // #if(==40)|#if(40==40)|#if($x==40) and #elseif/#else/#{end} — value from resolver('_'), constant from template
    const ifElseifElseEndRegex = /#if\s*\(\s*(?:==\s*(\d+)|\d+\s*==\s*(\d+)|\$\w+\s*==\s*(\d+))\s*\)([\s\S]*?)#(?:\{end\}|end\b)/gi
    result = result.replace(ifElseifElseEndRegex, (_fullBlock, eqConst1, eqConst2, eqConst3, blockInner) => {
      const firstConst = eqConst1 ?? eqConst2 ?? eqConst3
      const directiveRegex = /#(?:elseif\s*\(\s*(?:==\s*(\d+)|\d+\s*==\s*(\d+)|\$\w+\s*==\s*(\d+))\s*\)|\{else\}|else\b)/gi
      const branches: { type: 'eq' | 'var' | 'else'; value?: number; varName?: string; content: string }[] = []
      let pos = 0
      const firstCond: { type: 'eq'; value: number } = { type: 'eq', value: Number(firstConst) }
      let d
      while ((d = directiveRegex.exec(blockInner)) !== null) {
        const content = blockInner.slice(pos, d.index)
        if (branches.length === 0) {
          branches.push({
            type: firstCond.type,
            value: firstCond.value,
            content
          })
        } else {
          const elseifConst = d[1] ?? d[2] ?? d[3]
          if (elseifConst !== undefined && elseifConst !== '') {
            branches.push({ type: 'eq', value: Number(elseifConst), content })
          } else {
            branches.push({ type: 'else', content })
          }
        }
        pos = d.index + d[0].length
      }
      const lastContent = blockInner.slice(pos)
      if (branches.length === 0) {
        branches.push({
          type: firstCond.type,
          value: firstCond.value,
          content: blockInner
        })
      } else {
        const last = branches[branches.length - 1]
        ;(last as { content: string }).content = lastContent
      }
      const compareVal = resolver('_')
      let chosen = ''
      for (const b of branches) {
        if (b.type === 'eq') {
          const numCompare = Number(compareVal)
          if (numCompare === b.value || String(compareVal).trim() === String(b.value)) {
            chosen = b.content
            break
          }
        } else if (b.type === 'var') {
          const val = resolver(b.varName!)
          if (val !== null && val !== undefined && val !== '' && val !== false) {
            chosen = b.content
            break
          }
        } else {
          chosen = b.content
          break
        }
      }
      return chosen
    })

    const ifHasFieldRegex = /#if\s*\(\s*\$_\s*\.\s*hasField\s*\(\s*["']([^"']+)["']\s*\)\s*\)\s*([\s\S]*?)#(?:\{end\}|end\b)/gi
    result = result.replace(ifHasFieldRegex, (_full, fieldName, content) => {
      const ctx = resolver('_')
      const hasField = ctx != null && typeof (ctx as any).hasField === 'function' && (ctx as any).hasField(fieldName)
      return hasField ? content : ''
    })

    const ifBlockRegex = /#if\s*\(\s*(?:\$\{\s*(\w+)\s*\}|\$(\w+))\s*\)([^#]*?)#\{?end\}?/gi
    result = result.replace(ifBlockRegex, (_match, braceVarName, plainVarName, content) => {
      const value = resolver((braceVarName ?? plainVarName).trim())
      // If variable has a truthy value, include the content
      if (value !== null && value !== undefined && value !== '' && value !== false) {
        return content
      }
      return ''
    })

    const dollarBraceRegex = /\$\{\s*(?!_\.)(\w+)\s*\}/g
    result = result.replace(dollarBraceRegex, (_match, varName) => {
      const value = resolver(varName.trim())
      return value !== null && value !== undefined ? String(value) : ''
    })

    const dollarVarRegex = /\$(\w+)/g
    result = result.replace(dollarVarRegex, (match, varName, offset) => {
      // Check if this is part of ${var} or #if($var) - skip if so
      const beforeMatch = result.substring(Math.max(0, offset - 10), offset)
      if (beforeMatch.includes('#if') || beforeMatch.includes('${')) {
        return match // Keep original
      }
      // Check if followed by { or ) - skip if so
      const afterMatch = result.substring(offset + match.length, offset + match.length + 2)
      if (afterMatch.startsWith('{') || afterMatch.startsWith(')')) {
        return match // Keep original
      }
      const value = resolver(varName)
      return value !== null && value !== undefined ? String(value) : ''
    })

    return result
  },

  /**
   * Evaluates Velocity-like templates with #set, #foreach, #if, #break and method calls.
   * Supports: #set($selected = $positions.getRow(0))#foreach($pos in $positions)#if($pos.getFieldAsLong("main", 0) == 1)#set($selected = $pos)#break#end#end${selected.getFieldAsString("title", "-")}
   * @param template - Template string
   * @param context - Initial context ({ positions: UniDataTable }). Variables will be added in #set; context values can have methods like getRow(i), getFieldAsString(name, def), getFieldAsLong(name, def).
   * @returns Evaluated string
   */
  velocityFormat(template: string, context: Record<string, any>): string {
    if (!template || typeof template !== 'string') return template || ''

    const scope: Record<string, any> = { ...context }

    function parseArgs(argsStr: string, currentScope: Record<string, any>): any[] {
      const args: any[] = []
      let rest = argsStr.trim()
      while (rest.length) {
        const q = rest.match(/^\s*["']([^"']*)["']\s*(?:,\s*)?/)
        if (q) {
          args.push(q[1])
          rest = rest.slice(q[0].length)
          continue
        }
        const num = rest.match(/^\s*(\d+)\s*(?:,\s*)?/)
        if (num) {
          args.push(Number(num[1]))
          rest = rest.slice(num[0].length)
          continue
        }
        const ref = rest.match(/^\s*\$(\w+)\s*(?:,\s*)?/)
        if (ref) {
          args.push(currentScope[ref[1]])
          rest = rest.slice(ref[0].length)
          continue
        }
        rest = rest.replace(/^\s*,?\s*/, '')
        if (rest === rest.replace(/^\s*,?\s*/, '')) break
      }
      return args
    }

    function evalExpr(expr: string, currentScope: Record<string, any>): any {
      expr = expr.trim()
      const chain = expr.match(/^\$(\w+)((?:\.\w+\s*\([^)]*\))*)$/)
      if (!chain) return undefined
      let val: any = currentScope[chain[1]]
      const methodCalls = chain[2]
      if (!methodCalls) return val
      const re = /\.(\w+)\s*\(([^)]*)\)/g
      let m
      while ((m = re.exec(methodCalls)) !== null) {
        if (val == null) return undefined
        const methodName = m[1]
        const args = parseArgs(m[2], currentScope)
        const fn = val[methodName]
        if (typeof fn !== 'function') return undefined
        val = fn.apply(val, args)
      }
      return val
    }

    const result = template

    /** Find position of matching closing paren, or -1 */
    function findMatchingParen(s: string, openAt: number): number {
      if (s[openAt] !== '(') return -1
      let depth = 1
      for (let i = openAt + 1; i < s.length; i++) {
        if (s[i] === '(') depth++
        else if (s[i] === ')') {
          depth--
          if (depth === 0) return i
        }
      }
      return -1
    }

    /** Find position of matching #end (counting nested #if/#foreach and #end), starting after body start. Returns index of start of #end or -1 */
    function findMatchingEnd(s: string, start: number): number {
      let depth = 1
      let i = start
      while (i < s.length) {
        const rest = s.slice(i)
        const ifForeach = rest.match(/^#(if|foreach)\s*\b/)
        const endMatch = rest.match(/^#end\b/)
        if (ifForeach) {
          depth++
          i += ifForeach[0].length
          continue
        }
        if (endMatch) {
          depth--
          if (depth === 0) return i
          i += endMatch[0].length
          continue
        }
        i += 1
      }
      return -1
    }

    function run(tpl: string, innerScope: Record<string, any>): string {
      let out = ''
      let pos = 0

      while (pos < tpl.length) {
        const setNext = tpl.slice(pos).match(/^#set\s*\(\s*\$(\w+)\s*=\s*([^#]+?)\s*\)/)
        if (setNext) {
          const varName = setNext[1]
          const exprStr = setNext[2].trim()
          const val = evalExpr(exprStr.startsWith('$') ? exprStr : exprStr, innerScope)
          innerScope[varName] = val
          pos += setNext[0].length
          continue
        }

        const foreachStart = tpl.slice(pos).match(/^#foreach\s*\(\s*\$(\w+)\s+in\s+(\$[\w.()",\s]+)\s*\)\s*/)
        if (foreachStart) {
          const fullMatch = foreachStart[0]
          const iterVar = foreachStart[1]
          const collExpr = foreachStart[2].trim()
          const bodyStart = pos + fullMatch.length
          const endIdx = findMatchingEnd(tpl, bodyStart)
          if (endIdx === -1) {
            out += tpl[pos]
            pos += 1
            continue
          }
          let body = tpl.slice(bodyStart, endIdx)

          body = body.replace(/\s*\)\s*(?:#end\s*)?(?=\$\{)/, '')

          body = body.replace(/\s*\)\s*#end\s*$/, '').replace(/\s*\)\s*$/, '')
          let afterEnd = endIdx + 4
          const coll = evalExpr(collExpr, innerScope)
          const iterScope = innerScope
          if (coll != null && typeof coll.getRow === 'function') {
            const len = (coll as { rows?: unknown[] }).rows?.length ?? 0
            for (let i = 0; i < len; i++) {
              iterScope[iterVar] = coll.getRow(i)
              let chunk = run(body, iterScope)
              if (chunk.includes('__BREAK__')) {
                chunk = chunk.replace(/__BREAK__/g, '')
                out += chunk
                break
              }
              out += chunk
            }
          } else if (Array.isArray(coll)) {
            for (let i = 0; i < coll.length; i++) {
              iterScope[iterVar] = coll[i]
              let chunk = run(body, iterScope)
              if (chunk.includes('__BREAK__')) {
                chunk = chunk.replace(/__BREAK__/g, '')
                out += chunk
                break
              }
              out += chunk
            }
          }

          const afterLoop = tpl.slice(afterEnd)
          const skipParenEnd = afterLoop.match(/^\)\s*#end\s*/)
          if (skipParenEnd) afterEnd += skipParenEnd[0].length
          pos = afterEnd
          continue
        }

        const ifStart = tpl.slice(pos).match(/^#if\s*\(\s*/i)
        if (ifStart) {
          const openParenAt = pos + ifStart[0].length - 1
          const closeParenAt = findMatchingParen(tpl, openParenAt)
          if (closeParenAt === -1) {
            out += tpl[pos]
            pos += 1
            continue
          }
          const condStr = tpl.slice(openParenAt + 1, closeParenAt).trim()
          let bodyStart = closeParenAt + 1
          while (bodyStart < tpl.length && (tpl[bodyStart] === ')' || tpl[bodyStart] === ' ' || tpl[bodyStart] === '\t')) bodyStart++
          const endIdx = findMatchingEnd(tpl, bodyStart)
          if (endIdx === -1) {
            out += tpl[pos]
            pos += 1
            continue
          }
          const body = tpl.slice(bodyStart, endIdx)
          const afterEnd = endIdx + 4
          const condExpr = condStr.replace(/\s*==\s*1\s*$/, '').trim()
          const condVal = condExpr ? evalExpr(condExpr.startsWith('$') ? condExpr : '$' + condExpr, innerScope) : undefined
          const numMatch = condStr.match(/==\s*1\s*$/)
          const cond = numMatch ? (condVal === 1 || condVal === true) : Boolean(condVal)
          if (cond) out += run(body, innerScope)
          pos = afterEnd
          continue
        }

        const breakMatch = tpl.slice(pos).match(/^#break/)
        if (breakMatch) {
          out += '__BREAK__'
          pos += breakMatch[0].length
          continue
        }

        const refMatch = tpl.slice(pos).match(/^\$\{([^}]+)\}/)
        if (refMatch) {
          const expr = refMatch[1].trim()
          const val = evalExpr(expr.startsWith('$') ? expr : '$' + expr, innerScope)
          out += val != null && val !== undefined ? String(val) : ''
          pos += refMatch[0].length
          continue
        }

        const simpleRef = tpl.slice(pos).match(/^\$(\w+)(?=[^.\w]|$)/)
        if (simpleRef) {
          const val = innerScope[simpleRef[1]]
          out += val != null && val !== undefined ? String(val) : ''
          pos += simpleRef[0].length
          continue
        }


        const skipParenEndBeforeRef = tpl.slice(pos).match(/^\)\s*#end\s*(?=\$\{)/)
        if (skipParenEndBeforeRef) {
          pos += skipParenEndBeforeRef[0].length
          continue
        }

        const skipParenBeforeRef = tpl.slice(pos).match(/^\)\s*(?=\$\{)/)
        if (skipParenBeforeRef) {
          pos += skipParenBeforeRef[0].length
          continue
        }

        out += tpl[pos]
        pos += 1
      }
      return out
    }

    return run(result, scope)
  },
}

export default parseService
